import React, { lazy, Suspense, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormLayout } from '@/features/dashboard/pages/form/layout';
import { useDocument } from '@/shared/hooks/useDocument';
import Loading from '@/shared/components/loading/loading';
import { Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { FormDisplayMode } from '@/core/types/documents';
import { queryClient } from '@/core/query/query.client';

// All query-param keys owned by the modal system — stripped on close
const MODAL_PARAM_KEYS = [
    'formModal', 'formDocType', 'formAction', 'formDocNo', 'formDisplayMode', 'parentDocNo',
];

/**
 * Root-level modal that opens any form as an overlay by reading
 * formModal/formDocType/formAction/formDocNo/formDisplayMode query params.
 *
 * Driven entirely by the URL — no intermediate React state, no useModalsStack.
 * This means it works identically on a hard refresh and on SPA navigation.
 */
export const GlobalFormModal: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 767px)');

    const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const isModalForm = sp.get('formModal') === 'true';
    const docType = sp.get('formDocType') ?? '';
    const action = sp.get('formAction') ?? '';
    const docNo = sp.get('formDocNo') ?? undefined;
    const displayMode = (sp.get('formDisplayMode') as FormDisplayMode) ?? 'modal';

    // On mobile redirect to full-page navigation instead of a modal overlay
    useEffect(() => {
        if (isMobile && isModalForm && docType && action) {
            const formPath = docNo
                ? `/master/${docType}/${action}/${docNo}`
                : `/master/${docType}/${action}`;
            const newSP = new URLSearchParams(location.search);
            MODAL_PARAM_KEYS.forEach(k => newSP.delete(k));
            navigate(newSP.toString() ? `${formPath}?${newSP.toString()}` : formPath, { replace: true });
        }
    }, [isMobile, isModalForm, docType, action, docNo, navigate, location.search]);

    if (!isModalForm || !docType || !action || isMobile) return null;

    return (
        <ModalContent
            // key forces remount when the target form changes (different docType/action/docNo)
            key={`${docType}|${action}|${docNo ?? 'new'}`}
            docType={docType}
            action={action}
            docNo={docNo}
            displayMode={displayMode}
        />
    );
};

interface ModalContentProps {
    docType: string;
    action: string;
    docNo?: string;
    displayMode: FormDisplayMode;
}

const ModalContent: React.FC<ModalContentProps> = ({ docType, action, docNo, displayMode }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { documentConfig } = useDocument(docType);

    const formComponentName = documentConfig?.formPage ?? 'DefaultForm';

    // Stable lazy reference — recreated only when the resolved component name changes
    const FormComponent = useMemo(
        () =>
            lazy(() =>
                import('@/features/forms/pages')
                    .then(module => {
                        const component = module[formComponentName as keyof typeof module];
                        return component && typeof component === 'function'
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ? { default: component as React.ComponentType<any> }
                            : { default: () => <div>Component not found: {formComponentName}</div> };
                    })
                    .catch(() => ({ default: () => <div>Error loading form</div> }))
            ),
        [formComponentName]
    );

    const handleClose = () => {
        const newSP = new URLSearchParams(location.search);
        MODAL_PARAM_KEYS.forEach(k => newSP.delete(k));
        const newUrl = newSP.toString()
            ? `${location.pathname}?${newSP.toString()}`
            : location.pathname;
        navigate(newUrl, { replace: true });
        queryClient.invalidateQueries({ queryKey: ['master', docType], exact: false });
    };

    const actionHeading = action.charAt(0).toUpperCase() + action.slice(1);
    const modalTitle = `${documentConfig?.formName ?? docType} | ${actionHeading}`;

    return (
        <Modal
            opened={true}
            onClose={handleClose}
            title={modalTitle}
            size="95%"
            centered
            closeButtonProps={{ 'aria-label': 'Close modal' }}
            transitionProps={{ duration: 0 }}
            styles={{
                content: {
                    maxWidth: '95vw !important',
                    width: '95vw !important',
                    minHeight: '95vh !important',
                    height: '95vh !important',
                },
                body: {
                    height: 'calc(95vh - 60px) !important',
                    minHeight: 'calc(95vh - 60px) !important',
                    overflow: 'auto',
                },
            }}
        >
            <FormLayout
                displayMode={displayMode}
                opened={true}
                onClose={handleClose}
                modalSize="95%"
                hideModalWrapper={true}
            >
                <Suspense fallback={<Loading />}>
                    <FormComponent docType={docType} action={action} docNo={docNo} />
                </Suspense>
            </FormLayout>
        </Modal>
    );
};
